import lz from "lzutf8";

// TypeScript interfaces for better type safety
export interface CraftEditorQuery {
  serialize: () => string;
}

export interface CraftEditorActions {
  deserialize: (json: string) => void;
}

/**
 * Export the editor state as compressed base64 string
 * @param query - The Craft.js editor query object
 * @returns Compressed base64 encoded string
 */
export function exportState(query: CraftEditorQuery): string {
  const json = query.serialize();
  return lz.encodeBase64(lz.compress(json));
}

/**
 * Import and apply compressed state to editor
 * @param encoded - Compressed base64 encoded string
 * @param actions - The Craft.js editor actions object
 */
export function importState(
  encoded: string,
  actions: CraftEditorActions
): void {
  const json = lz.decompress(lz.decodeBase64(encoded));
  actions.deserialize(json);
}

/**
 * Save the compressed editor state to localStorage
 * @param query - The Craft.js editor query object
 * @param key - The localStorage key to use (default: 'craftjs-state')
 * @returns Boolean indicating success
 */
export function saveToLocalStorage(
  query: CraftEditorQuery,
  key: string = 'craftjs-state'
): boolean {
  try {
    const compressed = exportState(query);
    localStorage.setItem(key, compressed);
    return true;
  } catch (error) {
    console.error('Error saving editor state to localStorage:', error);
    return false;
  }
}

/**
 * Load the compressed editor state from localStorage
 * @param actions - The Craft.js editor actions object
 * @param key - The localStorage key to use (default: 'craftjs-state')
 * @returns Boolean indicating success
 */
export function loadFromLocalStorage(
  actions: CraftEditorActions,
  key: string = 'craftjs-state'
): boolean {
  try {
    const compressed = localStorage.getItem(key);
    if (!compressed) return false;
    
    importState(compressed, actions);
    return true;
  } catch (error) {
    console.error('Error loading editor state from localStorage:', error);
    return false;
  }
}

/**
 * Setup an auto-save functionality that saves the editor state periodically
 * @param query - The Craft.js editor query object
 * @param intervalMs - Milliseconds between auto-saves (default: 30000 - 30 seconds)
 * @param key - The localStorage key to use (default: 'craftjs-autosave')
 * @returns A function to cancel the auto-save interval
 */
export function setupAutoSave(
  query: CraftEditorQuery,
  intervalMs: number = 30000,
  key: string = 'craftjs-autosave'
): () => void {
  const intervalId = setInterval(() => {
    saveToLocalStorage(query, key);
  }, intervalMs);

  // Return a cleanup function
  return () => clearInterval(intervalId);
}

/**
 * Export the state to a downloadable file
 * @param query - The Craft.js editor query object
 * @param filename - Optional filename without extension (default: generates with date)
 */
export function downloadState(
  query: CraftEditorQuery,
  filename?: string
): void {
  try {
    const compressed = exportState(query);
    const defaultFilename = `landing-page-${new Date().toISOString().split('T')[0]}.lzpage`;
    
    // Create a blob and download link
    const blob = new Blob([compressed], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    
    link.download = filename || defaultFilename;
    link.href = url;
    link.click();
    
    // Clean up
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading editor state:', error);
  }
}

/**
 * Load state from an uploaded file
 * @param file - The file object from a file input
 * @param actions - The Craft.js editor actions object
 * @returns A promise that resolves to a boolean indicating success
 */
export function loadStateFromFile(
  file: File,
  actions: CraftEditorActions
): Promise<boolean> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        if (event.target?.result) {
          const compressed = event.target.result.toString();
          importState(compressed, actions);
          resolve(true);
        } else {
          resolve(false);
        }
      } catch (error) {
        console.error('Error loading state from file:', error);
        resolve(false);
      }
    };
    
    reader.onerror = () => {
      console.error('FileReader error');
      resolve(false);
    };
    
    reader.readAsText(file);
  });
}
